import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Download, Receipt, RefreshCw } from "lucide-react";
import { Badge, Button, Card, Tabs, TabsList, TabsTrigger } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { portal, pdfHref, type PortalInvoice } from "../../../lib/portal";
import { fmtDate, fmtAmount, invoiceStatusVariant } from "../../../lib/format";
import { relativeDay } from "../../../lib/due";
import { EmptyState, Figure, ListSkeleton, PageHead, RowButton } from "../../ui";
import { filterInvoices, invoiceTotals, isOverdue, isUnpaid, type InvoiceFilter } from "./lib";

/** The columns of a row from `md` up; the header row shares them. */
const COLS = "md:grid md:grid-cols-[minmax(0,1fr)_7.5rem_9rem_8rem_9.5rem] md:items-center md:gap-4";

const FILTERS: { value: InvoiceFilter; labelKey: string }[] = [
  { value: "all", labelKey: "portal.invoices.filterAll" },
  { value: "open", labelKey: "portal.invoices.filterOpen" },
  { value: "paid", labelKey: "portal.invoices.filterPaid" },
];

/**
 * Every invoice this client may read: what is owed above, the rows below.
 *
 * A row is one button that opens the invoice's page, with the PDF as a second,
 * separate target beside it - the one thing a bookkeeper wants without opening
 * anything. From `md` up the rows read as a table; on a phone each folds into
 * two lines with the amount and the status on the right, because a table that
 * scrolls sideways hides exactly those two.
 */
export function InvoiceList({ onOpen }: { onOpen: (id: string) => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [invoices, setInvoices] = useState<PortalInvoice[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<InvoiceFilter>("all");
  // One clock for the whole list, so two rows cannot disagree about "today".
  const now = useMemo(() => new Date(), []);

  function load() {
    setFailed(false);
    setInvoices(null);
    // A failed read says so. Falling through to the empty line would tell a
    // client they have no invoices, which this screen has no grounds to claim
    // when the request never answered.
    void portal.invoices().then(setInvoices).catch(() => setFailed(true));
  }
  useEffect(load, []);

  const totals = useMemo(() => (invoices ? invoiceTotals(invoices) : null), [invoices]);
  const rows = invoices ? filterInvoices(invoices, filter) : [];
  const count: Record<InvoiceFilter, number> = {
    all: invoices?.length ?? 0,
    open: totals?.openCount ?? 0,
    paid: totals?.paidCount ?? 0,
  };

  return (
    <div>
      <PageHead title={t("portal.invoices.title")} description={t("portal.invoices.lead")} />

      {invoices && invoices.length > 0 && totals && (
        <Card className="mb-4 flex flex-wrap items-center gap-x-10 gap-y-5 p-5 md:px-6">
          <Figure
            label={t("portal.overview.outstandingTotal")}
            value={fmtAmount(totals.outstandingCents, totals.currency, locale)}
          />
          {totals.overdueCount > 0 && (
            <>
              <div aria-hidden="true" className="w-px self-stretch bg-border max-sm:hidden" />
              <Figure
                label={t("portal.overview.overdue")}
                icon={<AlertCircle className="size-3.5 text-destructive" />}
                value={fmtAmount(totals.overdueCents, totals.currency, locale)}
              />
            </>
          )}
          {/* A filter, not navigation: it narrows the list under it and is not
              worth an address of its own. Kit tabs give it the arrow keys. */}
          <Tabs value={filter} onValueChange={(v) => setFilter(v as InvoiceFilter)} className="max-sm:w-full sm:ml-auto">
            <TabsList className="max-sm:w-full">
              {FILTERS.map(({ value, labelKey }) => (
                <TabsTrigger key={value} value={value} className="max-sm:flex-1">
                  {t(labelKey)}
                  <span className="font-medium tabular-nums opacity-70">{count[value]}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </Card>
      )}

      <Card className="overflow-hidden">
        {failed ? (
          <EmptyState
            icon={AlertCircle}
            title={t("portal.loadFailed")}
            action={
              <Button variant="outline" onClick={load}>
                <RefreshCw />
                {t("portal.retry")}
              </Button>
            }
          />
        ) : !invoices ? (
          <ListSkeleton rows={5} label={t("common.loading")} />
        ) : invoices.length === 0 ? (
          <EmptyState icon={Receipt} title={t("portal.invoices.emptyTitle")} body={t("portal.invoices.emptyBody")} />
        ) : (
          <>
            <div
              aria-hidden="true"
              className={`hidden border-b border-border bg-muted/40 py-2.5 pl-5 pr-[4.5rem] text-xs font-semibold uppercase tracking-wider text-foreground/70 ${COLS}`}
            >
              <span>{t("portal.invoices.one")}</span>
              <span>{t("portal.invoices.issued")}</span>
              <span>{t("portal.invoices.due")}</span>
              <span className="text-right">{t("portal.invoices.total")}</span>
              <span>{t("portal.status")}</span>
            </div>
            {rows.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-foreground/70">{t("portal.invoices.filterEmpty")}</p>
            ) : (
              <ul className="divide-y divide-border">
                {rows.map((inv) => {
                  const open = isUnpaid(inv);
                  const late = isOverdue(inv);
                  const total = fmtAmount(inv.totalCents, inv.currency, locale);
                  const due = fmtDate(inv.dueDate, locale);
                  const status = (
                    <Badge variant={invoiceStatusVariant(inv.status)}>
                      {late && <AlertCircle className="size-3" />}
                      {t(`portal.invoiceStatus.${inv.status}`)}
                    </Badge>
                  );
                  return (
                    <li key={inv.id} className="flex items-stretch">
                      <RowButton onClick={() => onOpen(inv.id)} className="min-w-0 flex-1 pr-2">
                        {/* From `md` up: one line, five columns. */}
                        <span className={`hidden w-full ${COLS}`}>
                          <span className="min-w-0">
                            <span className="block font-semibold text-primary">{inv.number}</span>
                            {inv.title && <span className="block truncate text-foreground/70">{inv.title}</span>}
                          </span>
                          <span className="tabular-nums text-foreground/70">{fmtDate(inv.issueDate, locale)}</span>
                          <span className={open ? "tabular-nums" : "tabular-nums text-foreground/70"}>
                            {due}
                            {/* How far away the deadline is, only while it still
                                matters: a paid invoice has no deadline left. */}
                            {open && inv.dueDate && (
                              <span className="block text-xs text-foreground/70">
                                {relativeDay(inv.dueDate, locale, now)}
                              </span>
                            )}
                          </span>
                          <span className="text-right font-semibold tabular-nums">{total}</span>
                          <span>{status}</span>
                        </span>
                        {/* Under `md`: two lines, the amount and the status on the right. */}
                        <span className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1.5 md:hidden">
                          <span className="min-w-0">
                            <span className="block font-semibold">{inv.number}</span>
                            {inv.title && <span className="block truncate text-foreground/70">{inv.title}</span>}
                          </span>
                          <span className="text-right font-semibold tabular-nums">{total}</span>
                          <span className="text-xs text-foreground/70">
                            {t("portal.invoices.due")} {due}
                          </span>
                          <span className="justify-self-end">{status}</span>
                        </span>
                      </RowButton>
                      <Button variant="ghost" size="icon" asChild className="my-auto mr-3 shrink-0">
                        <a
                          href={pdfHref(`/portal/invoices/${inv.id}/pdf`)}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={t("portal.pdfOf", { number: inv.number })}
                          title={t("portal.invoices.downloadPdf")}
                        >
                          <Download />
                        </a>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
